import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowRight, MagnifyingGlass, Package, Storefront, Warning } from 'shared/icons';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import ErrorCard from '../../ui/ErrorCard';
import Input from '../../ui/Input';
import { DateTimeText, Id, MONO_CLASS, Mono, Sku } from '../../ui/Mono';
import { SkeletonTable } from '../../ui/Skeleton';
import StandardTable from '../../ui/StandardTable';
import { useDebounce } from '../../../hooks/useDebounce';
import { ExtractPaging } from '../../../utils/aggregation';
import { ExtractListRows } from '../payload';
import KitLabelReprint from './KitLabelReprint';
import OriginChain from './OriginChain';
import { GetProductionLotGenealogy, GetSupplierLotGenealogy, ListProductionLots } from './productionApi';
import { FormatLabel, useProductionLabels } from './productionLabels';
import { FlattenGenealogySources, FormatQty } from './productionModel';

const DEFAULT_PAGE_SIZE = 30;
const PAGE_SIZE_OPTIONS = [15, 30, 50, 100];

/**
 * LOTES DE PRODUÇÃO E GENEALOGIA — a tela do RECALL.
 *
 * ── A pergunta que ela responde ─────────────────────────────────────────────
 * A pergunta de um recall não é "de onde veio este kit" (essa é a fácil), é a
 * INVERSA: *"o fornecedor avisou que o lote X tem problema — quais kits saíram
 * dele?"*. Por isso o campo de busca principal é o **lote do fornecedor**, e o
 * resultado é a lista de LTs que o consumiram; a genealogia (o caminho de
 * volta) é o detalhe de cada um.
 *
 * ── Por que o kit de transição aparece pelos DOIS lados ─────────────────────
 * Quando a mãe acaba no meio de um kit, aquele kit leva material dos dois lotes
 * e HERDA o LT vigente — os dois lotes de fornecedor ficam pendurados no mesmo
 * LT. Consequência prática, que esta tela precisa deixar explícita: buscar por
 * A **ou** por B traz o mesmo LT, e é isso que faz o recall alcançar o kit
 * misturado. Sem o aviso na tela, quem consulta acha que a lista trouxe um lote
 * errado e desconsidera justamente a caixa que deveria ser recolhida.
 *
 * ── Onde a cadeia termina (e por que) ───────────────────────────────────────
 * A árvore desce enquanto o "lote" consumido for o CÓDIGO DE OUTRO LT (um
 * intermediário fabricado aqui). Quando chega num lote que não é LT, é lote de
 * FORNECEDOR — e é aí que a cadeia FECHA: pelo pallet consumido, a consulta
 * alcança a ordem de compra e o fornecedor (`OriginChain`). Origem que não é
 * compra aparece dizendo o que é; nenhuma linha fica em branco.
 *
 * ── Os dois sentidos ────────────────────────────────────────────────────────
 * Buscar pelo lote do fornecedor mostra também o caminho de VOLTA (de qual
 * compra aquele lote entrou), acima da lista de LTs que o consumiram: as duas
 * perguntas do recall — "de onde veio" e "para onde foi" — na mesma tela.
 */
const ProductionLotsExplorer = ({ initialLot = null }) => {
    const { label } = useProductionLabels();
    const [supplierLotInput, setSupplierLotInput] = useState('');
    const [productionDay, setProductionDay] = useState('');
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [selectedLot, setSelectedLot] = useState(initialLot);
    const [genealogy, setGenealogy] = useState(null);
    const [genealogyError, setGenealogyError] = useState(null);
    const [genealogyLoading, setGenealogyLoading] = useState(false);
    // O sentido INVERSO: de onde o lote bipado na bobina entrou.
    const [supplierOrigins, setSupplierOrigins] = useState(null);

    const debouncedSupplierLot = useDebounce(supplierLotInput, 350);

    // Um lote escolhido em outra aba (ficha da OP) abre já selecionado aqui.
    useEffect(() => {
        if (initialLot) {
            setSelectedLot(initialLot);
        }
    }, [initialLot]);

    const load = useCallback(async () => {
        setLoadError(null);
        try {
            const offset = (Math.max(1, page) - 1) * Math.max(1, pageSize);
            const data = await ListProductionLots({
                limit: pageSize,
                offset,
                supplier_lot: debouncedSupplierLot.trim() || undefined,
                production_day: productionDay || undefined,
            });
            const list = ExtractListRows(data, ['production_lots']);
            setRows(list);
            setTotal(ExtractPaging(data, { total: list.length, limit: pageSize, offset }).total);
        } catch (error) {
            setLoadError(error?.message || 'Erro ao carregar');
            setRows([]);
        } finally {
            setHasLoadedOnce(true);
        }
    }, [page, pageSize, debouncedSupplierLot, productionDay]);

    useEffect(() => {
        load();
    }, [load]);

    /**
     * O CAMINHO DE VOLTA do lote digitado: de qual pallet — e de qual compra e
     * fornecedor — aquele lote entrou. Roda junto com a busca, porque é a mesma
     * pergunta feita do outro lado: quem recebe o aviso do fornecedor quer ver,
     * na mesma tela, se o lote realmente veio dele.
     */
    useEffect(() => {
        const supplierLot = debouncedSupplierLot.trim();
        if (!supplierLot) {
            setSupplierOrigins(null);
            return undefined;
        }
        let alive = true;
        GetSupplierLotGenealogy(supplierLot)
            .then((data) => {
                if (alive) {
                    setSupplierOrigins(data);
                }
            })
            // Falhar aqui não pode derrubar a busca de LTs, que é a função
            // principal da tela: o card simplesmente não aparece.
            .catch(() => {
                if (alive) {
                    setSupplierOrigins(null);
                }
            });
        return () => {
            alive = false;
        };
    }, [debouncedSupplierLot]);

    useEffect(() => {
        if (!selectedLot?.id) {
            setGenealogy(null);
            return undefined;
        }
        let alive = true;
        setGenealogyLoading(true);
        setGenealogyError(null);
        GetProductionLotGenealogy(selectedLot.id)
            .then((data) => {
                if (alive) {
                    setGenealogy(data);
                }
            })
            .catch((error) => {
                if (alive) {
                    setGenealogyError(error?.message || 'Erro ao carregar');
                    setGenealogy(null);
                }
            })
            .finally(() => {
                if (alive) {
                    setGenealogyLoading(false);
                }
            });
        return () => {
            alive = false;
        };
    }, [selectedLot]);

    const columns = useMemo(
        () => [
            {
                key: 'code',
                label: label('wms.field.production_lot_code'),
                getValue: (row) => row.code,
                render: (row) => <Id value={row.code} className="font-medium" />,
            },
            {
                key: 'production_order_code',
                label: label('wms.field.production_order_code'),
                getValue: (row) => row.production_order_code || '',
                render: (row) => <Mono className="text-xs">{row.production_order_code || '—'}</Mono>,
            },
            {
                key: 'production_day',
                label: label('wms.field.production_day'),
                getValue: (row) => row.production_day || '',
                render: (row) => <Mono className="text-xs">{row.production_day || '—'}</Mono>,
            },
            {
                key: 'created_at',
                label: label('wms.lpn.field.created_at'),
                getValue: (row) => row.created_at || '',
                render: (row) => <DateTimeText value={row.created_at} relative />,
            },
        ],
        [label],
    );

    const supplierLots = Array.isArray(genealogy?.supplier_lots) ? genealogy.supplier_lots : [];
    const sources = genealogy ? FlattenGenealogySources(genealogy) : [];
    const outputs = Array.isArray(genealogy?.outputs) ? genealogy.outputs : [];
    const consumptions = Array.isArray(genealogy?.consumptions) ? genealogy.consumptions : [];

    const listContent = !hasLoadedOnce ? (
        <SkeletonTable columns={4} rows={8} />
    ) : loadError ? (
        <ErrorCard
            title={label('wms.production.lots_title')}
            message={`${label('wms.common.load_error')}\n${loadError}`}
            action={label('wms.common.retry')}
            actionFn={load}
        />
    ) : (
        <StandardTable
            rows={rows}
            columns={columns}
            rowKey={(row, index) => row.id || row.code || index}
            onRowClick={(row) => setSelectedLot(row)}
            countLabel={label('wms.production.lots_count')}
            exportFileName="wms-lotes-producao"
            emptyMessage={label('wms.production.lots_empty')}
            paginationMode="server"
            totalItems={total}
            currentPage={page}
            onPageChange={setPage}
            itemsPerPage={pageSize}
            onItemsPerPageChange={setPageSize}
            defaultPageSize={DEFAULT_PAGE_SIZE}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
    );

    return (
        <section className="space-y-4">
            <div className="space-y-1">
                <h2 className="text-lg font-semibold text-black dark:text-white">{label('wms.production.lots_title')}</h2>
                <p className="max-w-3xl text-sm text-mono-600 dark:text-mono-400">
                    {label('wms.production.lots_description')}
                </p>
            </div>

            {/* O RECALL em destaque: é a razão de a tela existir. */}
            <div className="space-y-3 rounded-xl border-2 border-mono-300 bg-white p-4 dark:border-mono-700 dark:bg-mono-950">
                <div className="flex items-center gap-2">
                    <MagnifyingGlass size={18} className="text-mono-500" />
                    <h3 className="text-base font-bold text-black dark:text-white">
                        {label('wms.production.recall_title')}
                    </h3>
                </div>
                <p className="text-sm text-mono-600 dark:text-mono-400">{label('wms.production.recall_hint')}</p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Input
                        label={label('wms.production.supplier_lot_filter')}
                        value={supplierLotInput}
                        onChange={(event) => {
                            setSupplierLotInput(event.target.value.toUpperCase());
                            setPage(1);
                        }}
                        placeholder={label('wms.production.supplier_lot_placeholder')}
                        className={MONO_CLASS}
                        autoComplete="off"
                    />
                    <Input
                        label={label('wms.field.production_day')}
                        type="date"
                        value={productionDay}
                        onChange={(event) => {
                            setProductionDay(event.target.value);
                            setPage(1);
                        }}
                        className={MONO_CLASS}
                    />
                </div>

                {/* O SENTIDO INVERSO, na mesma tela: de qual pallet — e de qual
                    COMPRA e FORNECEDOR — o lote digitado entrou. Quem recebe o
                    aviso do fornecedor confere aqui se o lote é mesmo dele
                    antes de olhar o que saiu. */}
                {supplierOrigins ? (
                    <div className="space-y-2 rounded-lg border border-mono-200 bg-mono-50 p-3 dark:border-mono-800 dark:bg-mono-900/40">
                        <div className="flex items-center gap-2">
                            <Storefront size={16} className="text-mono-500" />
                            <h4 className="text-sm font-bold text-black dark:text-white">
                                {label('wms.production.origin_forward_title')}
                            </h4>
                        </div>
                        <p className="text-xs text-mono-500 dark:text-mono-400">
                            {label('wms.production.origin_forward_hint')}
                        </p>
                        {supplierOrigins.origins?.length ? (
                            <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                {supplierOrigins.origins.map((origin, index) => (
                                    <li
                                        key={`${origin.lpn_id ?? 'sem-pallet'}-${origin.resolution}-${index}`}
                                        className="rounded-lg border border-mono-200 bg-white p-2.5 dark:border-mono-800 dark:bg-mono-950"
                                    >
                                        <OriginChain origin={origin} label={label} />
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-sm text-mono-500 dark:text-mono-400">
                                {label('wms.production.origin_forward_empty')}
                            </p>
                        )}
                    </div>
                ) : null}
            </div>

            <div className="flex flex-col gap-4 xl:flex-row xl:items-stretch">
                <div className="min-w-0 flex-1">{listContent}</div>

                <aside className="w-full shrink-0 xl:w-[420px]">
                    <div className="h-full space-y-4 rounded-xl border border-mono-200 bg-white p-5 dark:border-mono-800 dark:bg-mono-950 xl:sticky xl:top-4">
                        {!selectedLot ? (
                            <p className="text-sm text-mono-500 dark:text-mono-400">
                                {label('wms.production.lot_select')}
                            </p>
                        ) : (
                            <>
                                <div>
                                    <p className="text-xs uppercase tracking-wide text-mono-500">
                                        {label('wms.production.genealogy')}
                                    </p>
                                    <Id value={selectedLot.code} className="text-lg font-bold" />
                                    <p className="text-xs text-mono-500 dark:text-mono-400">
                                        {selectedLot.production_order_code} · {selectedLot.production_day}
                                    </p>
                                </div>

                                {genealogyError ? (
                                    <p className="text-sm text-danger-600 dark:text-danger-400">{genealogyError}</p>
                                ) : null}
                                {genealogyLoading ? (
                                    <p className="text-sm text-mono-500 dark:text-mono-400">…</p>
                                ) : null}

                                {genealogy ? (
                                    <>
                                        {/* O AVISO DO KIT DE TRANSIÇÃO: dois lotes
                                            no mesmo LT não é erro de dado — é a
                                            regra funcionando. */}
                                        {supplierLots.length > 1 ? (
                                            <div className="flex items-start gap-2 rounded-lg border-2 border-warning-400 bg-warning-soft p-3 text-warning-text dark:border-warning-600 dark:bg-warning-900/30 dark:text-warning-200">
                                                <Warning size={20} className="mt-0.5 shrink-0" />
                                                <p className="text-sm font-semibold leading-snug">
                                                    {FormatLabel(label('wms.production.transition_lot_notice'), {
                                                        count: supplierLots.length,
                                                        lots: supplierLots.join(', '),
                                                    })}
                                                </p>
                                            </div>
                                        ) : null}

                                        <section className="space-y-2">
                                            <p className="text-xs uppercase tracking-wide text-mono-500">
                                                {label('wms.production.supplier_lots')}
                                            </p>
                                            <div className="flex flex-wrap gap-2">
                                                {supplierLots.length === 0 ? (
                                                    <span className="text-sm text-mono-500">—</span>
                                                ) : (
                                                    supplierLots.map((lot) => (
                                                        <Badge key={lot} variant="info">
                                                            <Mono>{lot}</Mono>
                                                        </Badge>
                                                    ))
                                                )}
                                            </div>
                                        </section>

                                        {/* O que SAIU — a resposta do recall. */}
                                        <section className="space-y-2">
                                            <p className="text-xs uppercase tracking-wide text-mono-500">
                                                {label('wms.production.outputs_of_lot')}
                                            </p>
                                            {outputs.length === 0 ? (
                                                <p className="text-sm text-mono-500 dark:text-mono-400">
                                                    {label('wms.production.no_outputs')}
                                                </p>
                                            ) : (
                                                <ul className="divide-y divide-mono-100 dark:divide-mono-900">
                                                    {outputs.map((output) => (
                                                        <li key={output.id} className="py-2">
                                                            <div className="flex items-start gap-2">
                                                                <Package size={16} className="mt-0.5 shrink-0 text-mono-500" />
                                                                <div className="min-w-0 flex-1">
                                                                    <Mono className="block text-sm font-medium text-black dark:text-white">
                                                                        {output.lpn_code || '—'}
                                                                    </Mono>
                                                                    <Sku value={output.sku} className="text-xs" />
                                                                    <span className="block truncate text-xs text-mono-500 dark:text-mono-400">
                                                                        {output.product_name || ''}
                                                                    </span>
                                                                </div>
                                                                <div className="shrink-0 text-right">
                                                                    <Mono className="block text-sm text-black dark:text-white">
                                                                        {FormatQty(output.qty)}
                                                                    </Mono>
                                                                    {output.kits_count ? (
                                                                        <span className="block text-xs text-mono-500">
                                                                            {`${output.kits_count} ${label('wms.field.kits_count').toLowerCase()}`}
                                                                        </span>
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                            {output.scrap_alert ? (
                                                                <Badge variant="warning" className="mt-1">
                                                                    {label('wms.production.scrap_alert')}
                                                                </Badge>
                                                            ) : null}
                                                            {/* ETIQUETA PERDIDA ≠ KIT SEM ETIQUETA:
                                                                a segunda via sai daqui, do
                                                                apontamento que a gerou. */}
                                                            <KitLabelReprint output={output} />
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </section>

                                        {/* O que ENTROU + a árvore de origem. */}
                                        <section className="space-y-2">
                                            <p className="text-xs uppercase tracking-wide text-mono-500">
                                                {label('wms.production.consumptions')}
                                            </p>
                                            {consumptions.length === 0 ? (
                                                <p className="text-sm text-mono-500 dark:text-mono-400">
                                                    {label('wms.production.no_consumptions')}
                                                </p>
                                            ) : (
                                                <ul className="divide-y divide-mono-100 dark:divide-mono-900">
                                                    {consumptions.map((consumption) => (
                                                        <li key={consumption.id} className="py-2">
                                                            <div className="flex items-start gap-2">
                                                                <div className="min-w-0 flex-1">
                                                                    <Mono className="block text-sm text-black dark:text-white">
                                                                        {consumption.supplier_lot}
                                                                    </Mono>
                                                                    <Sku value={consumption.sku} className="text-xs" />
                                                                </div>
                                                                <Mono className="shrink-0 text-sm text-black dark:text-white">
                                                                    {FormatQty(consumption.qty)}
                                                                </Mono>
                                                            </div>
                                                            {/* A CADEIA no próprio consumo: o pallet
                                                                que entregou aquela linha e, quando a
                                                                entrada foi compra, a OC e o
                                                                fornecedor. */}
                                                            <div className="mt-1 rounded border border-mono-100 p-2 dark:border-mono-900">
                                                                <p className="mb-1 text-[11px] uppercase tracking-wide text-mono-400">
                                                                    {label('wms.production.origin_chain')}
                                                                </p>
                                                                <OriginChain
                                                                    origin={consumption.lpn_origin}
                                                                    label={label}
                                                                />
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </section>

                                        {sources.length > 0 ? (
                                            <section className="space-y-2">
                                                <p className="text-xs uppercase tracking-wide text-mono-500">
                                                    {label('wms.production.tree_title')}
                                                </p>
                                                <ul className="space-y-2">
                                                    {sources.map((source, index) => (
                                                        <li
                                                            key={`${source.supplier_lot}-${index}`}
                                                            className="text-sm"
                                                            style={{ paddingLeft: `${source.depth * 16}px` }}
                                                        >
                                                            <div className="flex items-center gap-1.5">
                                                                <ArrowRight
                                                                    size={14}
                                                                    className="shrink-0 text-mono-400"
                                                                />
                                                                <Mono className="text-black dark:text-white">
                                                                    {source.supplier_lot}
                                                                </Mono>
                                                                <span className="text-xs text-mono-500 dark:text-mono-400">
                                                                    {source.kind === 'PRODUCTION_LOT'
                                                                        ? label('wms.production.source_production_lot')
                                                                        : source.kind === 'PRODUCTION_LOT_VISITED'
                                                                          ? label('wms.production.source_visited')
                                                                          : label('wms.production.source_supplier')}
                                                                </span>
                                                            </div>
                                                            {/* A PONTA: cada pallet que trouxe aquele
                                                                lote, com a compra e o fornecedor
                                                                quando a entrada veio de compra. */}
                                                            {source.origins.map((origin, originIndex) => (
                                                                <div
                                                                    key={`${origin.lpn_id ?? 'sem-pallet'}-${origin.resolution}-${originIndex}`}
                                                                    className="ml-5 mt-1 border-l border-mono-200 pl-2 dark:border-mono-800"
                                                                >
                                                                    <OriginChain origin={origin} label={label} compact />
                                                                </div>
                                                            ))}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </section>
                                        ) : null}

                                        <p className="border-t border-mono-200 pt-3 text-xs text-mono-500 dark:border-mono-800 dark:text-mono-400">
                                            {label('wms.production.po_note')}
                                        </p>
                                    </>
                                ) : null}

                                <Button variant="ghost" size="sm" onClick={() => setSelectedLot(null)}>
                                    {label('wms.common.close')}
                                </Button>
                            </>
                        )}
                    </div>
                </aside>
            </div>
        </section>
    );
};

export default ProductionLotsExplorer;
